import React, { useState, useRef, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import serviceLoomIcon from "../../assets/img/serviceloom-mark.png";
import { ReactComponent as TimerIcon } from "../../assets/svg/timer.svg";
import {
  LuLogOut as LogOut,
  LuChevronDown as ChevronDown,
} from "react-icons/lu";

// Shared portal canvas — same radial-on-#EDF1FE used by Super Admin / Project Admin.
const CANVAS_BG =
  "radial-gradient(ellipse 711px 1111px at 100% 25%, rgba(76, 53, 215, 0.4) 0%, rgba(233, 240, 254, 0) 70%), #E9F0FE";

/**
 * Manager portal layout. The Manager role exists only to approve SLA changes
 * proposed by their tagged Super Admin, so the shell is intentionally light on
 * navigation — but it follows the same theme as the Super Admin / Project Admin
 * portals: dark #2A2D3A sidebar with gradient-border active pill (#FF6B4A →
 * #603CFF, #206CEF text), transparent header, and blue accents.
 */
export default function ManagerLayout() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const name = storedUser.name || storedUser.full_name || "Manager";
  const email = storedUser.email || "";
  const initials =
    String(name)
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "M";

  const onLogout = () => {
    try { localStorage.clear(); } catch (e) { /* ignore */ }
    // DEF-178: sessionStorage holds chat/session state and survives
    // localStorage.clear() — clear it so the next user in this tab starts fresh.
    try { sessionStorage.clear(); } catch (e) { /* ignore */ }
    navigate("/auth/sign-in", { replace: true });
  };

  // Close the avatar dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navItem = ({ isActive }) =>
    `flex items-center h-[42px] w-[calc(100%-32px)] mx-auto my-1 pl-[15px] pr-[15px] gap-3
     rounded-full font-['IBM_Plex_Sans'] text-[14px] leading-none no-underline transition-all duration-200
     ${isActive
       ? "font-medium text-[#206CEF]"
       : "font-normal text-[#B4B6C2] hover:bg-white/5 hover:text-white"}`;

  return (
    <div
      className="flex h-screen w-full overflow-hidden"
      style={{
        background: CANVAS_BG,
        backdropFilter: "blur(90px)",
        WebkitBackdropFilter: "blur(90px)",
      }}
    >
      {/* Sidebar — dark, matches Super Admin / Project Admin */}
      <aside className="w-[210px] flex-shrink-0 flex flex-col bg-[#2A2D3A] shadow-lg border-r border-[#383B48]">
        {/* Brand */}
        <div className="h-[70px] flex items-center gap-2.5 pl-6 pr-4 bg-[#2A2D3A]">
          <img src={serviceLoomIcon} alt="ServiceLoom" className="h-7 w-7 object-contain flex-shrink-0" />
          <span className="text-white font-semibold text-lg">ServiceLoom</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 pt-2">
          <NavLink
            to="/admin/manager/sla-approvals"
            className={navItem}
            style={({ isActive }) =>
              isActive
                ? {
                    background:
                      "linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, #FF6B4A 0%, #603CFF 100%) border-box",
                    border: "1px solid transparent",
                  }
                : { border: "1px solid transparent" }
            }
          >
            <span className="flex items-center justify-center w-[20px] h-[20px] flex-shrink-0">
              <TimerIcon width={18} height={18} />
            </span>
            SLA Approvals
          </NavLink>
        </nav>
      </aside>

      {/* Main section */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden relative">
        {/* Header — transparent, floating, matches Super Admin */}
        <header className="w-full">
          <div className="flex pt-5 pb-2 px-6 items-center justify-between w-full" style={{ background: "transparent" }}>
            <div className="flex flex-col justify-center min-w-0">
              <div className="flex items-center gap-2">
                <i
                  className="pi pi-home text-gray-400 text-lg cursor-pointer transition-colors hover:text-[#3250FF]"
                  onClick={() => navigate("/admin/manager/sla-approvals")}
                  title="Home"
                />
                <h1 className="text-xl font-semibold text-[#0F172A] truncate">SLA Approvals</h1>
              </div>
            </div>

            <div className="relative ml-4" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center px-2 py-1 gap-4 justify-between rounded-full bg-[#FFFFFFB2] border border-[#EBE7F8] hover:bg-white transition-colors"
              >
                <div className="flex items-center w-9 h-9 bg-[#1B1730] rounded-full justify-center text-white text-xs font-bold flex-shrink-0">
                  {initials}
                </div>
                <div className="hidden sm:flex flex-col items-start leading-[1.1] min-w-0">
                  <span className="text-sm font-semibold text-[#1B1730] truncate w-full text-left">{name}</span>
                  <span className="text-xs text-gray-500 truncate w-full text-left mt-0.5">SLA approver</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-800 flex-shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3" style={{ background: "#FFFFFF", borderBottom: "1px solid #DDE3EA" }}>
                    <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
                    {email
                      ? <p className="text-xs text-slate-700 truncate mt-0.5">{email}</p>
                      : <p className="text-xs text-slate-500 truncate mt-0.5">SLA approver</p>}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="w-full px-5 py-6 min-h-full flex flex-col">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
